import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Employee } from "@/types/employee";
import { User, Mail, Briefcase, Calendar, UserPlus, Archive, LayoutGrid, List, Search, Building2, Phone } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import AddEmployeeDialog from "@/components/AddEmployeeDialog";
import { ArchivedEmployeesList } from "@/components/ArchivedEmployeesList";

const EmployeeListPage = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { user, userRole } = useAuth();

  // Only Admin and HR Manager can add employees - Vorarbeiter can only evaluate
  const canAddEmployee = userRole === "admin" || userRole === "hr_manager" || userRole === "hr_clerk";

  useEffect(() => {
    if (user) {
      loadEmployees();
    }
  }, [user]);

  const loadEmployees = async () => {
    try {
      // All authenticated users with proper permissions can access employees
      const { data, error } = await supabase
        .from("employees")
        .select("*")
        .is("archived_at", null)
        .order("name");
      
      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // For employees, only show their own profile
  const canViewArchive = userRole === "admin" || userRole === "hr_manager";

  // Filter employees based on search term
  const filteredEmployees = employees.filter(employee => {
    const search = searchTerm.toLowerCase();
    return (
      employee.name?.toLowerCase().includes(search) ||
      employee.position?.toLowerCase().includes(search) ||
      employee.department?.toLowerCase().includes(search) ||
      employee.employee_number?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-muted-foreground">Wird geladen...</p>
      </div>
    );
  }

  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredEmployees.map((employee) => (
        <Card
          key={employee.id}
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => navigate(`/employee/${employee.id}`)}
        >
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Avatar className="h-12 w-12 flex-shrink-0">
                <AvatarImage src={employee.profile_image_url} alt={employee.name} />
                <AvatarFallback className="bg-primary/10">
                  <User className="h-6 w-6 text-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg truncate">
                  {employee.name}
                </h3>
                <div className="space-y-1 mt-2">
                  {employee.position && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Briefcase className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{employee.position}</span>
                    </div>
                  )}
                  {employee.employee_number && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{employee.employee_number}</span>
                    </div>
                  )}
                  {employee.department && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{employee.department}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const ListView = () => (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12"></TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Position</TableHead>
            <TableHead className="hidden md:table-cell">Abteilung</TableHead>
            <TableHead className="hidden lg:table-cell">Personalnummer</TableHead>
            <TableHead className="hidden lg:table-cell">Telefon</TableHead>
            <TableHead className="hidden xl:table-cell">Eintrittsdatum</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredEmployees.map((employee) => (
            <TableRow
              key={employee.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => navigate(`/employee/${employee.id}`)}
            >
              <TableCell>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={employee.profile_image_url} alt={employee.name} />
                  <AvatarFallback className="bg-primary/10 text-xs">
                    <User className="h-4 w-4 text-primary" />
                  </AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell className="font-medium">{employee.name}</TableCell>
              <TableCell>{employee.position || "-"}</TableCell>
              <TableCell className="hidden md:table-cell">{employee.department || "-"}</TableCell>
              <TableCell className="hidden lg:table-cell">{employee.employee_number || "-"}</TableCell>
              <TableCell className="hidden lg:table-cell">
                {employee.phone || employee.mobile || "-"}
              </TableCell>
              <TableCell className="hidden xl:table-cell">
                {employee.employment_start_date 
                  ? new Date(employee.employment_start_date).toLocaleDateString("de-DE") 
                  : "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Mitarbeiter</h1>
          <p className="text-muted-foreground">
            Übersicht aller Mitarbeiter
          </p>
        </div>
        {canAddEmployee && (
          <Button onClick={() => setShowAddDialog(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Mitarbeiter hinzufügen
          </Button>
        )}
      </div>

      <Tabs defaultValue="active" className="space-y-6">
        <TabsList>
          <TabsTrigger value="active" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Aktive Mitarbeiter
            <Badge variant="secondary" className="ml-1">{employees.length}</Badge>
          </TabsTrigger>
          {canViewArchive && (
            <TabsTrigger value="archived" className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Archiv
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {/* Search and View Toggle */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Mitarbeiter suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <ToggleGroup 
              type="single" 
              value={viewMode} 
              onValueChange={(value) => value && setViewMode(value as "grid" | "list")}
              className="border rounded-lg p-1"
            >
              <ToggleGroupItem value="grid" aria-label="Kachelansicht" className="px-3">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="Listenansicht" className="px-3">
                <List className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Employee Count */}
          {searchTerm && (
            <p className="text-sm text-muted-foreground">
              {filteredEmployees.length} von {employees.length} Mitarbeitern
            </p>
          )}

          {/* View Content */}
          {viewMode === "grid" ? <GridView /> : <ListView />}

          {filteredEmployees.length === 0 && employees.length > 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Keine Mitarbeiter gefunden für "{searchTerm}"
                </p>
              </CardContent>
            </Card>
          )}

          {employees.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  Keine aktiven Mitarbeiter gefunden.
                </p>
                {canAddEmployee && (
                  <Button 
                    className="mt-4" 
                    onClick={() => setShowAddDialog(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Ersten Mitarbeiter hinzufügen
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {canViewArchive && (
          <TabsContent value="archived">
            <ArchivedEmployeesList />
          </TabsContent>
        )}
      </Tabs>

      <AddEmployeeDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={loadEmployees}
      />
    </div>
  );
};

export default EmployeeListPage;
